/* eslint-disable arrow-body-style */
import { ChannelList, useChatContext } from 'stream-chat-react';
import type { ReactNode } from 'react';

import { ChannelSearch } from '../ChannelSearch/ChannelSearch';
import { SupportChannelList } from '../SupportChannelList/SupportChannelList';
import { JobSupportChannelGroups } from '../JobSupportChannelList/JobSupportChannelGroups';
import { JobChannelList } from '../JobSupportChannelList/JobSupportChannelList';
import { TeamChannelList } from '../TeamChannelList/TeamChannelList';
import { ChannelPreview } from '../ChannelPreview/ChannelPreview';
import { sortChannelsByRecentActivity } from '../../utils/channelOrdering';

import type { Channel, ChannelFilters, ChannelSort } from 'stream-chat';

import { StreamChatType } from '../../types';

const filters: ChannelFilters[] = [{ type: 'team' }];
const cleanerSupportFilters: ChannelFilters = {
  type: { $in: ['cleanerSupport'] },
};
const clientSupportFilters: ChannelFilters = {
  type: { $in: ['clientSupport'] },
};
const jobSupportFilters: ChannelFilters = {
  type: { $in: ['jobSupport'] },
};
const options = { state: true, watch: true, presence: true, limit: 100 };
const sort: ChannelSort<StreamChatType> = {
  last_message_at: -1,
  updated_at: -1,
};

const customChannelTeamFilter = (channels: Channel[]) => {
  return channels.filter((channel) => channel.type === 'team');
};

const customChannelMessagingFilter = (channels: Channel[]) => {
  return channels.filter((channel) => channel.type === 'messaging');
};

const customChannelCleanerSupportFilter = (channels: Channel[]) => {
  return channels.filter((channel) => channel.type === 'cleanerSupport');
};

const customChannelClientSupportFilter = (channels: Channel[]) => {
  return channels.filter((channel) => channel.type === 'clientSupport');
};

const customChannelJobSupportFilter = (channels: Channel[]) => {
  return channels.filter((channel) => channel.type === 'jobSupport');
};

const renderGroupedJobSupportChannels = (
  channels: Channel<StreamChatType>[],
  renderChannel: (channel: Channel<StreamChatType>) => ReactNode
) => (
  <JobSupportChannelGroups channels={channels} renderChannel={renderChannel} />
);

const renderStableChannels = (
  channels: Channel<StreamChatType>[],
  renderChannel: (channel: Channel<StreamChatType>) => ReactNode
) => <>{sortChannelsByRecentActivity(channels).map(renderChannel)}</>;

type ChannelListProps = {
  activeChannelId?: string;
};

const TeamChannelsList = () => (
  <ChannelList
    channelRenderFilterFn={customChannelTeamFilter}
    filters={filters[0]}
    options={options}
    sort={sort}
    List={(listProps) => <TeamChannelList {...listProps} type="team" />}
    Preview={(previewProps) => <ChannelPreview {...previewProps} type="team" />}
    renderChannels={renderStableChannels}
  />
);

const MessagingChannelsList = () => (
  <ChannelList
    channelRenderFilterFn={customChannelMessagingFilter}
    filters={filters[1]}
    options={options}
    sort={sort}
    setActiveChannelOnMount={false}
    List={(listProps) => <TeamChannelList {...listProps} type="messaging" />}
    Preview={(previewProps) => (
      <ChannelPreview {...previewProps} type="messaging" />
    )}
    renderChannels={renderStableChannels}
  />
);

const CleanersChannelsList = ({ activeChannelId }: ChannelListProps) => (
  <ChannelList
    channelRenderFilterFn={customChannelCleanerSupportFilter}
    customActiveChannel={activeChannelId}
    filters={cleanerSupportFilters}
    key={`cleanerSupport-${activeChannelId ?? 'default'}`}
    options={options}
    sort={sort}
    setActiveChannelOnMount={false}
    List={(listProps) => (
      <SupportChannelList {...listProps} type="cleanerSupport" />
    )}
    Preview={(previewProps) => (
      <ChannelPreview {...previewProps} type="cleanerSupport" />
    )}
    EmptyStateIndicator={() => (
      <div className="str-chat-channel-list_empty">
        No conversations here yet
      </div>
    )}
    renderChannels={renderStableChannels}
  />
);

const ClientsChannelsList = ({ activeChannelId }: ChannelListProps) => (
  <ChannelList
    channelRenderFilterFn={customChannelClientSupportFilter}
    customActiveChannel={activeChannelId}
    filters={clientSupportFilters}
    key={`clientSupport-${activeChannelId ?? 'default'}`}
    options={options}
    sort={sort}
    setActiveChannelOnMount={false}
    List={(listProps) => (
      <SupportChannelList {...listProps} type="clientSupport" />
    )}
    Preview={(previewProps) => (
      <ChannelPreview {...previewProps} type="clientSupport" />
    )}
    EmptyStateIndicator={() => (
      <div className="str-chat-channel-list_empty">
        No conversations here yet
      </div>
    )}
    renderChannels={renderStableChannels}
  />
);

const JobsSupportChannelsList = ({ activeChannelId }: ChannelListProps) => (
  <ChannelList
    channelRenderFilterFn={customChannelJobSupportFilter}
    customActiveChannel={activeChannelId}
    filters={jobSupportFilters}
    key={`jobSupport-${activeChannelId ?? 'default'}`}
    options={options}
    sort={sort}
    setActiveChannelOnMount={false}
    List={(listProps) => <JobChannelList {...listProps} type="jobSupport" />}
    Preview={(previewProps) => (
      <ChannelPreview {...previewProps} type="jobSupport" />
    )}
    renderChannels={renderGroupedJobSupportChannels}
    EmptyStateIndicator={() => (
      <div className="str-chat-channel-list_empty">
        No conversations here yet
      </div>
    )}
  />
  // <ChannelList
  //   channelRenderFilterFn={customChannelCleanerSupportFilter}
  //   filters={jobSupportFilters}
  //   options={options}
  //   sort={sort}
  //   setActiveChannelOnMount={false}
  //   List={(listProps) => <SupportChannelList {...listProps} type="messaging" />}
  //   Preview={(previewProps) => (
  //     <ChannelPreview {...previewProps} type="jobSupport" />
  //   )}
  //   EmptyStateIndicator={() => (
  //     <div className="str-chat-channel-list_empty">
  //       No conversations here yet
  //     </div>
  //   )}
  // />
);

export const Sidebar = () => {
  const { channel: activeChannel } = useChatContext<StreamChatType>();
  const getActiveChannelId = (type: string) =>
    activeChannel?.type === type ? activeChannel.id : undefined;

  return (
    <div className="sidebar">
      <div className="channel-list-bar">
        <ChannelSearch />
        <TeamChannelsList />
        <MessagingChannelsList />
        <CleanersChannelsList
          activeChannelId={getActiveChannelId('cleanerSupport')}
        />
        <ClientsChannelsList
          activeChannelId={getActiveChannelId('clientSupport')}
        />
        <JobsSupportChannelsList
          activeChannelId={getActiveChannelId('jobSupport')}
        />
      </div>
    </div>
  );
};
